import { useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";

import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { deleteClient } from "@/api/index.js";
import { Client } from "@/types";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface DeleteClientProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (defaultInfo?: Client) => void;
  onCancel: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: Client; //
}

const DeleteClientModal: React.FC<DeleteClientProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await deleteClient(defaultInfo?.client_id);
      enqueueSnackbar(`Client Deleted.`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Deleted Client: ${defaultInfo?.name}`,
        event: "client:delete",
        object_type: "client",
        object_uuid: defaultInfo?.uuid,
      });

      onSubmit?.(defaultInfo);
      navigate("/clients");
    } catch (error) {
      console.error("Error deleting client:", error);
      enqueueSnackbar(`Error deleting client.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Client</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete the client:{" "}
        <span style={{ fontWeight: 800 }}>"{defaultInfo?.name}"</span>?
        <p>
          This client will be deleted and unassigned from all associated cases.
        </p>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Client"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default DeleteClientModal;
